import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import BookingWizard from "@/components/BookingWizard";
import AdminDashboard from "@/components/AdminDashboard";
import BookingConfirmation from "@/components/BookingConfirmation";
import ContactModal from "@/components/ContactModal";
import { Shield, Clock, Star, ChevronLeft, ChevronRight, Eye, Sparkles, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Elegant Texas Lone Star - Subtle outline version
const TexasLoneStar = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className={className}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

// Filled Texas Star for accents
const TexasStarFilled = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

// GMC Yukon XL Images - New professional photos
const VEHICLE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_luxury-ride-book-2/artifacts/7yg0t87b_ChatGPT%20Image%20Mar%204%2C%202026%20at%2012_13_24%20PM.png",
  "https://customer-assets.emergentagent.com/job_luxury-ride-book-2/artifacts/mv44209u_IMG_7918.jpg",
  "https://customer-assets.emergentagent.com/job_luxury-ride-book-2/artifacts/nw0atw9n_ChatGPT%20Image%20Mar%204%2C%202026%20at%2012_20_36%20PM.png"
];

const Home = () => {
  const navigate = useNavigate();
  const [showBooking, setShowBooking] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Auto-rotate images
  useEffect(() => {
    if (!showBooking) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % VEHICLE_IMAGES.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [showBooking]);

  const premiumFeatures = [
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Uncompromising Safety",
      description: "Industry-leading safety protocols ensure your security and peace of mind are always our highest priority."
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      title: "Immaculate Luxury Fleet",
      description: "Step into a meticulously maintained, fully equipped premium SUV designed for your ultimate comfort."
    },
    {
      icon: <Star className="w-5 h-5" />,
      title: "Executive Chauffeurs",
      description: "Highly trained, courteous, and dedicated professionals focused on delivering a flawless journey."
    },
    {
      icon: <Eye className="w-5 h-5" />,
      title: "Strict Privacy & Discretion",
      description: "Confidentiality is guaranteed. Enjoy a quiet, private environment perfect for relaxation or business."
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Bespoke Flexibility",
      description: "From multi-stop corporate itineraries to special events, rides are tailored to your exact needs."
    },
    {
      icon: <DollarSign className="w-5 h-5" />,
      title: "Transparent Pricing",
      description: "Enjoy premium reliability with honest, guaranteed rates. Zero hidden fees, no surge pricing."
    }
  ];

  const handleBookingComplete = (booking) => {
    navigate(`/confirmation/${booking.id}`);
  };

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % VEHICLE_IMAGES.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + VEHICLE_IMAGES.length) % VEHICLE_IMAGES.length);

  return (
    <div className="min-h-screen quiet-luxury-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <TexasStarFilled size={18} className="text-champagne/70" />
            <span className="logo-text">IVARGE</span>
            <span className="logo-divider"></span>
            <span className="logo-subtitle">LUXURY RIDE</span>
          </Link>
          <div className="flex items-center gap-8">
            <button 
              onClick={() => setShowContact(true)}
              className="nav-link hidden md:block"
              data-testid="contact-btn"
            >
              Contact
            </button>
            <button 
              onClick={() => setShowBooking(true)}
              className="btn-luxury"
              data-testid="book-now-nav-btn"
            >
              Book Now
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16">
        <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12 w-full">
          {!showBooking ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
              {/* Left - Text Content */}
              <div className="animate-fade-in-up">
                <p className="text-champagne text-xs tracking-[0.35em] uppercase mb-8 font-light">
                  Premium Transportation
                </p>
                
                <h1 className="hero-title">
                  Elevate Your Image.
                  <span className="block text-champagne-gradient mt-2">Take Control of Your Time.</span>
                </h1>
                
                <p className="text-silver text-base md:text-lg max-w-xl mb-12 font-light leading-[1.8]">
                  Our hourly chauffeured service gives you full control of your schedule, without compromising on comfort or style. Whether for business, protocol, weddings, or exclusive private events, having a high-end SUV and a professional driver on demand transforms the entire experience. For custom itineraries or special event coordination, please{' '}
                  <button 
                    onClick={() => setShowContact(true)} 
                    className="text-champagne hover:text-champagne-light underline underline-offset-2 transition-colors"
                  >
                    contact us directly
                  </button>
                  {' '}to tailor every detail to your exact needs.
                </p>

                <div className="flex flex-col sm:flex-row gap-5 mb-12">
                  <button 
                    onClick={() => setShowBooking(true)}
                    className="btn-luxury-primary"
                    data-testid="reserve-your-ride-btn"
                  >
                    Reserve Your Ride
                  </button>
                  <button 
                    onClick={() => setShowContact(true)}
                    className="btn-luxury-secondary"
                    data-testid="contact-us-btn"
                  >
                    Contact Us
                  </button>
                </div>
              </div>

              {/* Right - Vehicle Image Gallery */}
              <div className="relative animate-fade-in">
                <div className="vehicle-image-container">
                  <img 
                    src={VEHICLE_IMAGES[currentImageIndex]} 
                    alt="GMC Yukon XL"
                    className="w-full h-full object-cover transition-opacity duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-transparent" />
                  
                  <button 
                    onClick={prevImage}
                    className="gallery-nav-btn left-4"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="gallery-nav-btn right-4"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="vehicle-label">
                      <p className="font-serif text-xl text-white tracking-wide">GMC Yukon XL</p>
                      <p className="text-silver/60 text-xs tracking-[0.2em] uppercase mt-1">Premium Full-Size SUV</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-3 mt-6">
                  {VEHICLE_IMAGES.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`gallery-dot ${currentImageIndex === index ? 'active' : ''}`}
                      aria-label={`View image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-full max-w-4xl mx-auto animate-fade-in pt-8">
              <BookingWizard 
                apiUrl={API} 
                onComplete={handleBookingComplete}
                onCancel={() => setShowBooking(false)}
              />
            </div>
          )}
        </div>
      </section>

      {/* Premium Experience Section */}
      {!showBooking && (
        <section className="py-32 px-8 lg:px-12 relative">
          <div className="section-divider" />
          
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-20">
              <div className="flex items-center justify-center gap-4 mb-6">
                <TexasLoneStar size={14} className="text-champagne/40" />
                <p className="text-champagne text-xs tracking-[0.35em] uppercase font-light">Excellence in Every Detail</p>
                <TexasLoneStar size={14} className="text-champagne/40" />
              </div>
              <h2 className="section-title">
                The Premium Experience
              </h2>
              <p className="text-silver/50 max-w-2xl mx-auto text-base font-light leading-relaxed">
                Every detail is crafted to deliver an exceptional journey
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {premiumFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="feature-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`premium-feature-${index}`}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3 className="font-serif text-lg text-white mb-3 tracking-wide">{feature.title}</h3>
                  <p className="text-silver/50 text-sm leading-relaxed font-light">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Airport Protocol Section */}
      {!showBooking && (
        <section className="py-24 px-8 lg:px-12 relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-px bg-champagne/20" />
              <TexasLoneStar size={12} className="text-champagne/40" />
              <div className="w-12 h-px bg-champagne/20" />
            </div>
            <h3 className="font-serif text-2xl text-white mb-4 tracking-wide">
              Seamless Airport Transfers
            </h3>
            <p className="text-silver/50 text-sm font-light leading-relaxed max-w-2xl mx-auto">
              Real-time flight tracking and exclusive service at DFW, Dallas Love Field, and JSX. We monitor your arrival so your chauffeur is always ready, offering 40 minutes of complimentary wait time for all airport pickups.
            </p>
          </div>
        </section>
      )}

      {/* Service Area Section */}
      {!showBooking && (
        <section className="py-32 px-8 lg:px-12 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Texas Stars Decoration */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <TexasStarFilled size={10} className="text-champagne/30" />
              <TexasStarFilled size={14} className="text-champagne/50" />
              <TexasStarFilled size={10} className="text-champagne/30" />
            </div>
            
            <p className="text-champagne text-xs tracking-[0.35em] uppercase mb-6 font-light">Service Coverage</p>
            <h2 className="section-title mb-8">
              Executive DFW Service.<br />Statewide Reach.
            </h2>
            <p className="text-silver/50 max-w-3xl mx-auto text-base font-light leading-relaxed mb-10">
              Proudly based in the Dallas-Fort Worth Metroplex, providing first-class hourly service and airport transfers. For long-distance itineraries, our premium SUV is available for comfortable, private city-to-city transfers anywhere your schedule demands across Texas.
            </p>
            
            {/* Subtle Texas Star Line */}
            <div className="flex items-center justify-center gap-6 text-champagne/20">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-champagne/20" />
              <TexasLoneStar size={16} className="text-champagne/30" />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-champagne/20" />
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA Section */}
      {!showBooking && (
        <section className="py-24 px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="cta-container">
              <p className="text-champagne text-xs tracking-[0.35em] uppercase mb-6 font-light">Get Started</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 tracking-wide">
                Ready to Experience Premium?
              </h2>
              <p className="text-silver/50 mb-10 max-w-xl mx-auto font-light leading-relaxed">
                Contact us directly or book your ride online. Available 24/7 for all your transportation needs.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
                <a 
                  href="tel:6823228644"
                  className="contact-link"
                >
                  <span className="text-lg tracking-wide">(682) 322-8644</span>
                </a>
                <span className="hidden sm:block text-white/10">|</span>
                <a 
                  href="mailto:reservationsluxury@ivarge.com"
                  className="contact-link"
                >
                  <span className="text-lg tracking-wide">reservationsluxury@ivarge.com</span>
                </a>
              </div>
              <button 
                onClick={() => setShowBooking(true)}
                className="btn-luxury-primary"
                data-testid="book-now-cta-btn"
              >
                Book Your Ride Now
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-16 px-8 lg:px-12 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-2">
            <TexasStarFilled size={14} className="text-champagne/50" />
            <span className="footer-logo">IVARGE</span>
            <span className="footer-divider"></span>
            <span className="footer-subtitle">LUXURY RIDE</span>
          </div>
          <div className="flex items-center gap-8 text-silver/40 text-sm tracking-wide">
            <a href="tel:6823228644" className="hover:text-champagne transition-colors duration-300">(682) 322-8644</a>
            <TexasLoneStar size={12} className="text-champagne/20 hidden md:block" />
            <a href="mailto:reservationsluxury@ivarge.com" className="hover:text-champagne transition-colors duration-300">Email Us</a>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-center gap-4 text-center">
          <p className="text-silver/25 text-xs tracking-wide font-light">
            Fully Licensed, Permitted, and Commercially Insured in the State of Texas.
          </p>
          <span className="hidden md:block text-white/10">|</span>
          <p className="text-silver/30 text-xs tracking-wide">
            © 2026 Ivarge LLC. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContact && <ContactModal onClose={() => setShowContact(false)} apiUrl={API} />}
    </div>
  );
};

function App() {
  return (
    <div className="min-h-screen quiet-luxury-bg">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/confirmation/:bookingId" element={<BookingConfirmation />} />
          <Route path="/success/:bookingId" element={<BookingConfirmation />} />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

export default App;
